import { useMsal } from "@azure/msal-react";
import { Col, Row } from "react-bootstrap";

import { useState, useEffect } from "react";
import AutocompleteFloatLabel from "../../../components/AutocompleteFloatLabel";
import DialogMessage from "../../../components/DialogMessage";
import DialogMessageProps from "../../../model/DialogMessageProps";
import Cartao from "../../../model/dto/Cartao";
import CartaoParams from "../../../model/params/CartaoParams";
import CartaoTipo from "../../../model/dto/CartaoTipo";
import { TypeDialogMessageEnum } from "../../../model/Enums/TypeDialogMessageEnum";
import SelectOption from "../../../model/SelectOption";
import ButtonStyles from "../../../styles/components/ButtonStyles";
import DialogStyles from "../../../styles/components/DialogStyles";
import GenericUtils from "../../../utils/GenericUtils";
import InputCalendarStyles from "../../../styles/components/InputCalendarStyles";
import CartoesService from "../../../service/Docente/CartoesService";
import { TipoCartaoEnum } from "../../../model/Enums/TipoCartaoEnum";
import StringUtils from "../../../utils/types/StringUtils";
import { MASK_DATE } from "../../../utils/constants";
import Professor from "../../../model/dto/Professor";

const { BtnPrimary } = ButtonStyles;
const { StyledContainerButton } = DialogStyles;

interface IFormReposicaoProps {
    onCreate: (typeCard: TipoCartaoEnum) => void;
    teacher: Professor;
    refreshScheduleCard: (cartaoAgendaId: number[]) => void;
}

function FormReposicao(props: IFormReposicaoProps) {
    const { instance } = useMsal();

    const [datePicker, setDatePicker] = useState<any>(null);

    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);

    const [card, setCard] = useState(new Array<SelectOption>());
    const [cartaoOptions, setCartaoOptions] = useState(
        new Array<SelectOption>()
    );
    const [cartaoList, setCartaoList] = useState(new Array<Cartao>());
    const [originalCard, setOriginalCard] = useState<number>(0);
    const [createdCard, setCreatedCard] = useState<number>(0);

    const isFormValid = !!card.length;

    const clearActivitiesList = () => {
        setCard([]);
        setCartaoList([]);
        setCartaoOptions([]);
    };

    const getCartoes = async () => {
        if (!datePicker) return;

        const { teacher } = props;

        const cartaoParams = GenericUtils.impl<CartaoParams>({
            atividadesReposicao: true,
            tipoCartaoId: [
                TipoCartaoEnum.AtividadesAvulsas,
                TipoCartaoEnum.Intrajornada,
                TipoCartaoEnum.HoraExtraImportacao,
                TipoCartaoEnum.HoraExtraTela,
            ],
            dataCartao: StringUtils.formatDateString(
                datePicker?.toISOString(),
                MASK_DATE
            ),
            docenteId: teacher.docenteID,
        });

        const response = await CartoesService.getCartoes(
            instance,
            cartaoParams
        );

        if (GenericUtils.isSuccessStatus(response)) {
            const { cartoes } = response.data;

            if (!cartoes?.length) {
                setDialogMessageProps({
                    title: "Não há cartões para reposição",
                    show: true,
                    type: TypeDialogMessageEnum.Info,
                });
                clearActivitiesList();
                return;
            }

            setCartaoList(cartoes);
            setCartaoOptions(
                cartoes.map((item: Cartao) => {
                    return {
                        label: `${item.titulo}`,
                        labelSecondary: `${item.horaInicio} - ${
                            item.horaFim
                        } | ${item.abrangencia?.nome} | ${
                            item.duracaoHoraAula === 60 ? "1 h/a" : "0,5 h/a"
                        }`,
                        value: item.cartaoAgendaId,
                    };
                })
            );
        } else {
            clearActivitiesList();
            setDialogMessageProps(GenericUtils.getPropsDialogError(response));
        }
    };

    const handleSubmit = async () => {
        if (!isFormValid) return;

        const cartao = cartaoList.find(
            (item) => item.cartaoAgendaId === card[0].value
        );

        if (!cartao) return;

        cartao.origemCartaoId = Number(cartao.cartaoAgendaId);
        cartao.cartaoAgendaId = undefined;
        cartao.data = undefined;
        cartao.horaInicio = undefined;
        cartao.horaFim = undefined;
        cartao.cartaoTipoOrigem = cartao.cartaoTipo;
        cartao.cartaoTipo = GenericUtils.impl<CartaoTipo>({
            cartaoTipoId: TipoCartaoEnum.Reposicao,
        });

        const response = await CartoesService.salvarCartao(instance, cartao);

        if (GenericUtils.isSuccessStatus(response)) {
            const createdCardId = Number(
                response.headers.location.replace(/\D/g, "")
            );

            const originalCardId = Number(cartao.origemCartaoId);

            setOriginalCard(originalCardId);
            setCreatedCard(createdCardId);

            setDialogMessageProps({
                show: true,
                type: TypeDialogMessageEnum.Success,
                title: "Cartão salvo com sucesso",
            });
        } else {
            setDialogMessageProps(GenericUtils.getPropsDialogError(response));
        }
    };

    const handleCloseDialogMessage = () => {
        if (dialogMessageProps.type === TypeDialogMessageEnum.Success) {
            props.refreshScheduleCard([createdCard, originalCard]);
            props.onCreate(TipoCartaoEnum.Reposicao);
        }

        setDialogMessageProps({} as DialogMessageProps);
    };

    useEffect(() => {
        if (!datePicker) return;
        getCartoes();
    }, [datePicker]);

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            onClose={handleCloseDialogMessage}
        />
    );

    return (
        <>
            <Row className="mb-4">
                <Col>
                    <InputCalendarStyles.StyledCalendar
                        placeholder="Data da atividade"
                        dateFormat="dd/mm/yy"
                        locale="pt-BR"
                        autoZIndex
                        touchUI
                        readOnlyInput
                        showButtonBar
                        maxDate={new Date()}
                        value={datePicker}
                        onChange={(e: any) => setDatePicker(e.target?.value)}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <AutocompleteFloatLabel
                        label="Selecione a atividade"
                        options={cartaoOptions}
                        selected={card}
                        onChange={setCard}
                        labelSecondary
                        className="modal-create-card-autocomplete"
                    />
                </Col>
            </Row>
            <StyledContainerButton className="mt-3">
                <BtnPrimary disabled={!isFormValid} onClick={handleSubmit}>
                    Criar
                </BtnPrimary>
            </StyledContainerButton>
            {dialogMessage}
        </>
    );
}

export default FormReposicao;
